import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

/**
 * First-screen overlay shown on the PWA build only. Listens for
 * `pwa-precache` custom events emitted by registerServiceWorker.js and
 * renders a progress bar while the service worker is downloading the
 * game assets for offline play.
 *
 * Hidden on non-PWA builds (REACT_APP_BUILD_TARGET !== 'pwa') and once
 * the precache reports completion.
 */
export default function PwaDownloadOverlay() {
  const [state, setState] = useState({ active: false, loaded: 0, total: 0, done: false });

  useEffect(() => {
    if (process.env.REACT_APP_BUILD_TARGET !== 'pwa') return undefined;
    if (typeof window === 'undefined') return undefined;

    const onEvent = (e) => {
      const data = e && e.detail;
      if (!data) return;
      if (data.type === 'pwa-precache-start') {
        setState({ active: true, loaded: 0, total: data.total || 0, done: false });
      } else if (data.type === 'pwa-precache-progress') {
        setState((s) => ({ ...s, active: true, loaded: data.loaded || 0, total: data.total || s.total }));
      } else if (data.type === 'pwa-precache-done') {
        setState({ active: false, loaded: data.loaded || 0, total: data.total || 0, done: true });
      }
    };

    window.addEventListener('pwa-precache', onEvent);
    return () => window.removeEventListener('pwa-precache', onEvent);
  }, []);

  if (!state.active || state.done || state.total === 0) return null;

  const pct = Math.min(100, Math.round((state.loaded / state.total) * 100));

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col items-center gap-3 mt-4"
        style={{ minWidth: 220 }}
      >
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">
          Downloading
        </div>
        <div
          style={{
            width: 220,
            height: 6,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ type: 'tween', duration: 0.3 }}
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, #8b5cf6, #e879f9)',
            }}
          />
        </div>
        <div className="text-[11px] text-white/40 tabular-nums">
          {state.loaded} / {state.total} ({pct}%)
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
